class TextPillsBlock extends HTMLElement {
  constructor() {
    super();
    this.onTriggerClick = this.onTriggerClick.bind(this);
    this.onDocumentClick = this.onDocumentClick.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
    this.onResize = this.onResize.bind(this);
    this.activeItem = null;
    this.productDetailsEl = null;
    this.hadOverflowHiddenClass = false;
  }

  connectedCallback() {
    this.items = [...this.querySelectorAll('[data-text-pill-item]')];
    this.triggers = [...this.querySelectorAll('[data-text-pill-trigger]')];

    this.triggers.forEach((trigger) => {
      trigger.addEventListener('click', this.onTriggerClick);
    });

    document.addEventListener('click', this.onDocumentClick);
    document.addEventListener('keydown', this.onKeydown);
    window.addEventListener('resize', this.onResize);
  }

  disconnectedCallback() {
    this.triggers?.forEach((trigger) => {
      trigger.removeEventListener('click', this.onTriggerClick);
    });

    document.removeEventListener('click', this.onDocumentClick);
    document.removeEventListener('keydown', this.onKeydown);
    window.removeEventListener('resize', this.onResize);
    this.closeAll();
  }

  onTriggerClick(event) {
    event.preventDefault();
    event.stopPropagation();

    const item = event.currentTarget.closest('[data-text-pill-item]');
    if (!item) return;

    if (this.activeItem === item) {
      this.closeItem(item);
      this.unlockProductDetailsOverflow();
      return;
    }

    this.closeAll();
    this.openItem(item);
  }

  onDocumentClick(event) {
    if (!this.activeItem) return;
    if (this.activeItem.contains(event.target)) return;
    this.closeAll();
  }

  onKeydown(event) {
    if (event.key === 'Escape') this.closeAll();
  }

  onResize() {
    if (this.activeItem) {
      this.updatePlacement(this.activeItem);
    }
  }

  getProductDetailsEl() {
    return this.closest('.main-product__details');
  }

  lockProductDetailsOverflow() {
    const details = this.getProductDetailsEl();
    if (!details) return;

    this.productDetailsEl = details;
    this.hadOverflowHiddenClass = details.classList.contains('overflow-hidden');

    if (this.hadOverflowHiddenClass) {
      details.classList.remove('overflow-hidden');
    }

    details.classList.add('text-pills-bubble-open');
  }

  unlockProductDetailsOverflow() {
    if (!this.productDetailsEl) return;

    this.productDetailsEl.classList.remove('text-pills-bubble-open');

    if (this.hadOverflowHiddenClass) {
      this.productDetailsEl.classList.add('overflow-hidden');
    }

    this.productDetailsEl = null;
    this.hadOverflowHiddenClass = false;
  }

  getMainTrigger(item) {
    return item.querySelector(
      '[data-text-pill-trigger]:not(.product-text-pills__bubble-close)'
    );
  }

  resetBubblePosition(bubble) {
    if (!bubble) return;

    bubble.style.removeProperty('left');
    bubble.style.removeProperty('transform');
    bubble.style.removeProperty('--bubble-arrow-left');
  }

  openItem(item) {
    const trigger = this.getMainTrigger(item);
    const bubble = item.querySelector('[data-text-pill-bubble]');
    if (!trigger || !bubble) return;

    if (!this.activeItem) {
      this.lockProductDetailsOverflow();
    }

    item.classList.add('is-open');
    trigger.setAttribute('aria-expanded', 'true');
    bubble.removeAttribute('hidden');
    bubble.setAttribute('aria-hidden', 'false');
    this.activeItem = item;

    requestAnimationFrame(() => this.updatePlacement(item));
  }

  closeItem(item) {
    const trigger = this.getMainTrigger(item);
    const bubble = item.querySelector('[data-text-pill-bubble]');

    item.classList.remove('is-open', 'is-below');
    trigger?.setAttribute('aria-expanded', 'false');
    bubble?.setAttribute('hidden', '');
    bubble?.setAttribute('aria-hidden', 'true');
    this.resetBubblePosition(bubble);

    if (this.activeItem === item) {
      this.activeItem = null;
    }
  }

  closeAll() {
    if (!this.activeItem) return;

    this.items.forEach((item) => this.closeItem(item));
    this.activeItem = null;
    this.unlockProductDetailsOverflow();
  }

  updatePlacement(item) {
    const trigger = this.getMainTrigger(item);
    const bubble = item.querySelector('[data-text-pill-bubble]');
    if (!trigger || !bubble) return;

    const viewportPadding = 12;

    item.classList.remove('is-below');
    this.resetBubblePosition(bubble);

    bubble.style.left = '50%';
    bubble.style.transform = 'translateX(-50%)';

    const triggerRect = trigger.getBoundingClientRect();
    let bubbleRect = bubble.getBoundingClientRect();

    if (bubbleRect.top < viewportPadding) {
      item.classList.add('is-below');
      bubbleRect = bubble.getBoundingClientRect();
    }

    const itemRect = item.getBoundingClientRect();
    const triggerCenterX = triggerRect.left + triggerRect.width / 2;
    const maxLeftInViewport =
      window.innerWidth - viewportPadding - bubbleRect.width;

    let bubbleLeftInViewport = triggerCenterX - bubbleRect.width / 2;
    bubbleLeftInViewport = Math.max(
      viewportPadding,
      Math.min(bubbleLeftInViewport, maxLeftInViewport)
    );

    bubble.style.left = `${bubbleLeftInViewport - itemRect.left}px`;
    bubble.style.transform = 'none';
    bubble.style.setProperty(
      '--bubble-arrow-left',
      `${triggerCenterX - bubbleLeftInViewport}px`
    );
  }
}

if (!customElements.get('text-pills-block')) {
  customElements.define('text-pills-block', TextPillsBlock);
}
